import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { SubscriptionPlan, PromoDiscount } from "@/types/subscription";
import {
  Check,
  PenIcon,
  Tag,
  Edit2,
  Trash2,
  Sparkles,
  Calendar,
  Clock,
  Percent,
  DollarSign,
  ChevronDown,
  CreditCard,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  formatDuration,
  getMonthlyEquivalent,
  getPlanDiscountSavings,
  calculateFinalPrice,
  getDailyEquivalent,
} from "@/api/subscription-plans";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatCurrency } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useState, useEffect } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface PlanCardProps {
  plan: SubscriptionPlan;
  onEdit: (plan: SubscriptionPlan) => void;
  onDelete: (plan: SubscriptionPlan) => void;
  onToggleActive: (plan: SubscriptionPlan) => void;
  activePromotions?: PromoDiscount[];
  selectedPromotion?: PromoDiscount | null;
}

// Helper function to format currency based on currency code
const formatPriceWithCurrency = (
  amount: number,
  currencyCode: string
): string => {
  const currencyMap: Record<string, string> = {
    INR: "₹",
    USD: "$",
    MMK: "K",
  };

  const currency = currencyMap[currencyCode] || currencyMap.INR;
  return `${currency}${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export function PlanCard({
  plan,
  onEdit,
  onDelete,
  onToggleActive,
  activePromotions = [],
  selectedPromotion = null,
}: PlanCardProps) {
  // Add state for selected currency and payment method
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    number | null
  >(null);

  // Get all available active currency configurations
  const getAvailableCurrencies = () => {
    if (!plan.priceConfigurations || plan.priceConfigurations.length === 0) {
      return [];
    }

    // Get all active configurations
    const activeConfigs = plan.priceConfigurations.filter((pc) => pc.isActive);

    // Extract unique currency codes
    const uniqueCurrencies = Array.from(
      new Set(activeConfigs.map((pc) => pc.currencyCode))
    );

    return uniqueCurrencies;
  };

  const availableCurrencies = getAvailableCurrencies();

  // Validate and auto-select payment method when currency changes
  useEffect(() => {
    if (selectedCurrency) {
      const availablePaymentMethods =
        getPaymentMethodsForCurrency(selectedCurrency);

      // If no payment method is selected or the selected one is not available for this currency
      if (
        !selectedPaymentMethodId ||
        !availablePaymentMethods.some((pm) => pm.id === selectedPaymentMethodId)
      ) {
        // Auto-select the first available payment method
        if (availablePaymentMethods.length > 0) {
          setSelectedPaymentMethodId(availablePaymentMethods[0].id);
        } else {
          setSelectedPaymentMethodId(null);
        }
      }
    }
  }, [selectedCurrency]);

  // Get payment methods for a specific currency
  const getPaymentMethodsForCurrency = (currencyCode: string) => {
    if (!plan.priceConfigurations || plan.priceConfigurations.length === 0) {
      return [];
    }

    // Debug logging
    console.log(
      `[PlanCard] Getting payment methods for currency: ${currencyCode}`
    );
    console.log(`[PlanCard] Plan configurations:`, plan.priceConfigurations);

    // Filter active configurations for the specific currency that have valid payment methods
    const filteredConfigs = plan.priceConfigurations.filter((pc) => {
      const isActive = pc.isActive;
      const currencyMatches = pc.currencyCode === currencyCode;
      const hasPaymentMethod = pc.paymentMethod && pc.paymentMethodId !== null;
      const paymentMethodActive = pc.paymentMethod?.isActive;
      const currencySupported =
        pc.paymentMethod?.supportedCurrencies?.includes(currencyCode);

      console.log(`[PlanCard] Checking config ID ${pc.id}:`, {
        paymentMethodName: pc.paymentMethod?.name,
        isActive,
        currencyMatches,
        hasPaymentMethod,
        paymentMethodActive,
        supportedCurrencies: pc.paymentMethod?.supportedCurrencies,
        currencySupported,
        passes:
          isActive &&
          currencyMatches &&
          hasPaymentMethod &&
          paymentMethodActive &&
          currencySupported,
      });

      return (
        isActive &&
        currencyMatches &&
        hasPaymentMethod &&
        paymentMethodActive &&
        pc.paymentMethod.supportedCurrencies &&
        currencySupported
      );
    });

    // Remove duplicates by payment method ID and return formatted data without processing fees
    const uniquePaymentMethods = filteredConfigs.reduce((acc, pc) => {
      const existingMethod = acc.find((item) => item.id === pc.paymentMethodId);
      if (!existingMethod && pc.paymentMethod) {
        // Use base price without processing fees (fees are for internal use only)
        acc.push({
          id: pc.paymentMethodId!,
          name: pc.paymentMethod.name || "Unknown",
          price: pc.price, // Base price without fees
          currencyCode: pc.currencyCode,
        });
      }
      return acc;
    }, [] as Array<{ id: number; name: string; price: number; currencyCode: string }>);

    console.log(
      `[PlanCard] Final payment methods for ${currencyCode}:`,
      uniquePaymentMethods
    );
    return uniquePaymentMethods;
  };

  // Get price configuration based on selected currency and payment method
  const getPriceConfiguration = () => {
    if (!plan.priceConfigurations || plan.priceConfigurations.length === 0) {
      return { price: 0, currencyCode: "INR" };
    }

    // Filter active configurations first
    const activeConfigs = plan.priceConfigurations.filter((pc) => pc.isActive);
    if (activeConfigs.length === 0) {
      return { price: 0, currencyCode: "INR" };
    }

    // If both currency and payment method are selected, try to find exact match
    if (selectedCurrency && selectedPaymentMethodId) {
      const exactMatch = activeConfigs.find(
        (pc) =>
          pc.currencyCode === selectedCurrency &&
          pc.paymentMethodId === selectedPaymentMethodId
      );
      if (exactMatch) return exactMatch;
    }

    // If only currency is selected, find any config with that currency
    if (selectedCurrency) {
      const selectedConfig = activeConfigs.find(
        (pc) => pc.currencyCode === selectedCurrency
      );
      if (selectedConfig) {
        // If we found a config with selected currency but no specific payment method
        // was selected yet, select this payment method
        if (!selectedPaymentMethodId && selectedConfig.paymentMethodId) {
          setSelectedPaymentMethodId(selectedConfig.paymentMethodId);
        }
        return selectedConfig;
      }
    }

    // Otherwise use default priority: INR → USD → MMK
    const inrConfig = activeConfigs.find((pc) => pc.currencyCode === "INR");
    if (inrConfig) {
      if (!selectedCurrency) setSelectedCurrency("INR");
      if (!selectedPaymentMethodId)
        setSelectedPaymentMethodId(inrConfig.paymentMethodId);
      return inrConfig;
    }

    const usdConfig = activeConfigs.find((pc) => pc.currencyCode === "USD");
    if (usdConfig) {
      if (!selectedCurrency) setSelectedCurrency("USD");
      if (!selectedPaymentMethodId)
        setSelectedPaymentMethodId(usdConfig.paymentMethodId);
      return usdConfig;
    }

    const mmkConfig = activeConfigs.find((pc) => pc.currencyCode === "MMK");
    if (mmkConfig) {
      if (!selectedCurrency) setSelectedCurrency("MMK");
      if (!selectedPaymentMethodId)
        setSelectedPaymentMethodId(mmkConfig.paymentMethodId);
      return mmkConfig;
    }

    // If none of the preferred currencies, return the first active config
    if (!selectedCurrency) setSelectedCurrency(activeConfigs[0].currencyCode);
    if (!selectedPaymentMethodId)
      setSelectedPaymentMethodId(activeConfigs[0].paymentMethodId);
    return activeConfigs[0];
  };

  const priceConfig = getPriceConfiguration();

  // Use base price without platform fees (fees are for internal use only)
  const basePrice = priceConfig.price;
  const currencyCode = priceConfig.currencyCode || "INR";

  // Set initial selected currency if not already set
  // This is a side effect that happens once when the component renders
  if (!selectedCurrency && currencyCode) {
    setSelectedCurrency(currencyCode);
  }

  // Calculate discount amount only if there's an actual discount
  const planDiscountAmount =
    plan.planDiscountPercentage && plan.planDiscountPercentage > 0
      ? basePrice * (plan.planDiscountPercentage / 100)
      : 0;

  // Calculate prices with the selected price configuration
  const priceAfterPlanDiscount = basePrice - planDiscountAmount;

  // Calculate promotional discount if applicable
  let finalPrice = priceAfterPlanDiscount;
  let promoDiscountAmount = 0;

  if (selectedPromotion && selectedPromotion.isActive) {
    if (selectedPromotion.discountType === "percentage") {
      promoDiscountAmount =
        priceAfterPlanDiscount * (selectedPromotion.discountValue / 100);
    } else {
      promoDiscountAmount = Math.min(
        priceAfterPlanDiscount,
        selectedPromotion.discountValue
      );
    }
    finalPrice = priceAfterPlanDiscount - promoDiscountAmount;
  }

  const totalSavings = planDiscountAmount + promoDiscountAmount;

  // Calculate monthly and daily equivalents
  const monthlyPrice = getMonthlyEquivalent(
    finalPrice,
    plan.duration,
    plan.durationType
  );
  const dailyPrice = getDailyEquivalent(
    finalPrice,
    plan.duration,
    plan.durationType
  );

  // Fix: Promotion is applicable if applicablePlans is empty (all plans) or includes this plan's ID
  const applicablePromotions = activePromotions.filter(
    (promo) =>
      !promo.applicablePlans ||
      promo.applicablePlans.length === 0 ||
      promo.applicablePlans.includes(plan.id)
  );

  const isPromotionSelected =
    selectedPromotion &&
    applicablePromotions.some((promo) => promo.id === selectedPromotion.id);

  return (
    <Card
      className={cn(
        "w-full transition-all duration-200 hover:shadow-lg flex flex-col h-full",
        !plan.isActive && "opacity-75",
        isPromotionSelected && "ring-2 ring-primary"
      )}
    >
      <CardHeader className="space-y-1 pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">{plan.name}</h3>
            <Badge variant={plan.isActive ? "default" : "secondary"}>
              {plan.isActive ? "Active" : "Inactive"}
            </Badge>
            {/* Only show discount badge if discount is greater than 0 */}
            {plan.planDiscountPercentage > 0 && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700">
                <Percent className="h-3 w-3 mr-1" />
                {plan.planDiscountPercentage}% off
              </Badge>
            )}
          </div>
        </div>
        <p className="text-sm text-muted-foreground">{plan.description}</p>
      </CardHeader>

      <CardContent className="pb-3 space-y-4 flex-grow">
        {/* Price Section with improved UI */}
        <div className="bg-muted/10 p-4 rounded-lg">
          <div className="space-y-4">
            {/* Currency & Payment Method Selection - Improved UI */}
            {availableCurrencies.length > 0 && (
              <div className="flex items-center gap-4">
                <div>
                  {/* Currency Selector */}
                  {availableCurrencies.length > 1 ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1 text-sm font-normal"
                        >
                          <strong>{selectedCurrency}</strong>
                          <ChevronDown className="h-4 w-4 opacity-50" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-[100px]">
                        {availableCurrencies.map((currency) => (
                          <DropdownMenuItem
                            key={currency}
                            className={cn(
                              "cursor-pointer",
                              currency === selectedCurrency &&
                                "font-medium bg-muted"
                            )}
                            onClick={() => {
                              setSelectedCurrency(currency);
                              setSelectedPaymentMethodId(null);
                            }}
                          >
                            {currency === selectedCurrency && (
                              <Check className="mr-2 h-4 w-4" />
                            )}
                            {currency !== selectedCurrency && (
                              <div className="mr-6" />
                            )}
                            {currency}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <div className="text-sm">
                      <strong>{selectedCurrency}</strong>
                    </div>
                  )}
                </div>

                {/* Payment Method Selector */}
                {selectedCurrency &&
                  getPaymentMethodsForCurrency(selectedCurrency).length > 0 && (
                    <div>
                      {getPaymentMethodsForCurrency(selectedCurrency).length >
                      1 ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 gap-1 text-sm font-normal"
                            >
                              <CreditCard className="h-4 w-4 mr-1" />
                              {getPaymentMethodsForCurrency(
                                selectedCurrency
                              ).find((pm) => pm.id === selectedPaymentMethodId)
                                ?.name || "Select Payment"}
                              <ChevronDown className="h-4 w-4 opacity-50" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="start"
                            className="w-[220px]"
                          >
                            {getPaymentMethodsForCurrency(selectedCurrency).map(
                              (pm) => (
                                <DropdownMenuItem
                                  key={pm.id}
                                  className={cn(
                                    "cursor-pointer",
                                    pm.id === selectedPaymentMethodId &&
                                      "font-medium bg-muted"
                                  )}
                                  onClick={() =>
                                    setSelectedPaymentMethodId(pm.id)
                                  }
                                >
                                  <div className="flex items-center justify-between w-full">
                                    <span>{pm.name}</span>
                                    <Badge variant="outline">
                                      {formatPriceWithCurrency(
                                        pm.price,
                                        pm.currencyCode
                                      )}
                                    </Badge>
                                  </div>
                                </DropdownMenuItem>
                              )
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <div className="text-sm flex items-center gap-1">
                          <CreditCard className="h-4 w-4" />
                          <strong>
                            {
                              getPaymentMethodsForCurrency(selectedCurrency)[0]
                                ?.name
                            }
                          </strong>
                        </div>
                      )}
                    </div>
                  )}
              </div>
            )}

            {/* Pricing information with improved spacing */}
            <div className="space-y-3 pt-2">
              {/* Only show original price with strikethrough if there's a discount */}
              {plan.planDiscountPercentage > 0 ? (
                <>
                  {/* Original price with plan discount */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm line-through text-muted-foreground">
                      {formatPriceWithCurrency(basePrice, currencyCode)}
                    </span>
                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200 border-none">
                      <DollarSign className="h-3 w-3 mr-1" />
                      Standard Price
                    </Badge>
                  </div>

                  {/* Price after plan discount */}
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "text-sm",
                        isPromotionSelected
                          ? "line-through text-muted-foreground"
                          : "font-medium"
                      )}
                    >
                      {formatPriceWithCurrency(
                        priceAfterPlanDiscount,
                        currencyCode
                      )}
                    </span>
                    <Badge
                      variant="outline"
                      className="bg-blue-50 text-blue-700 border-blue-200"
                    >
                      <Percent className="h-3 w-3 mr-1" />
                      Plan Discount: {plan.planDiscountPercentage}% off
                    </Badge>
                  </div>
                </>
              ) : null}

              {/* Final price with promo */}
              {isPromotionSelected && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">
                    {formatPriceWithCurrency(finalPrice, currencyCode)}
                  </span>
                  <Badge
                    variant="outline"
                    className="bg-orange-50 text-orange-700 border-orange-200"
                  >
                    <Tag className="h-3 w-3 mr-1" />
                    +Promo:{" "}
                    {selectedPromotion.discountType === "percentage"
                      ? `${selectedPromotion.discountValue}% off`
                      : `${formatPriceWithCurrency(
                          selectedPromotion.discountValue,
                          currencyCode
                        )} off`}
                  </Badge>
                </div>
              )}

              {/* Final price display - made more prominent */}
              <div className="flex items-baseline gap-2 pt-1">
                <span className="text-3xl font-bold">
                  {formatPriceWithCurrency(finalPrice, currencyCode)}
                </span>
                <span className="text-sm text-muted-foreground">
                  /{formatDuration(plan.duration, plan.durationType)}
                </span>
              </div>

              {monthlyPrice > 0 && (
                <p className="text-sm text-muted-foreground">
                  {formatPriceWithCurrency(monthlyPrice, currencyCode)}/month (
                  {formatPriceWithCurrency(dailyPrice, currencyCode)}
                  /day)
                </p>
              )}

              {/* Only show savings if there are actual savings */}
              {totalSavings > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <Badge
                    variant="secondary"
                    className="bg-green-100 text-green-700"
                  >
                    <Sparkles className="h-3 w-3 mr-1" />
                    Total savings:{" "}
                    {formatPriceWithCurrency(totalSavings, currencyCode)} (
                    {Math.round((totalSavings / basePrice) * 100)}%)
                    {planDiscountAmount > 0 && promoDiscountAmount > 0 && (
                      <span className="ml-1 text-xs">
                        (
                        {formatPriceWithCurrency(
                          planDiscountAmount,
                          currencyCode
                        )}{" "}
                        plan +{" "}
                        {formatPriceWithCurrency(
                          promoDiscountAmount,
                          currencyCode
                        )}{" "}
                        promo)
                      </span>
                    )}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Promotion Sections */}
        {isPromotionSelected && selectedPromotion && (
          <div className="w-full space-y-2 p-3 bg-primary/5 rounded-lg">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-primary/10 text-primary">
                <Tag className="h-3 w-3 mr-1" />
                {selectedPromotion.name}
              </Badge>
              <Badge
                variant="secondary"
                className="bg-green-100 text-green-700"
              >
                {selectedPromotion.discountType === "percentage"
                  ? `${selectedPromotion.discountValue}% off`
                  : `${formatPriceWithCurrency(
                      selectedPromotion.discountValue,
                      currencyCode
                    )} off`}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>
                  Valid until{" "}
                  {format(new Date(selectedPromotion.endDate), "MMM d, yyyy")}
                </span>
              </div>
              {selectedPromotion.maxUses > 0 && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>
                    {selectedPromotion.maxUses - selectedPromotion.currentUses}{" "}
                    uses remaining
                  </span>
                </div>
              )}
            </div>
            {selectedPromotion.description && (
              <p className="text-xs text-muted-foreground mt-1">
                {selectedPromotion.description}
              </p>
            )}
          </div>
        )}

        {applicablePromotions.length > 0 && !isPromotionSelected && (
          <div className="w-full space-y-2">
            <p className="text-xs font-medium text-muted-foreground">
              Available Promotions:
            </p>
            <div className="space-y-1">
              {applicablePromotions.map((promo) => (
                <Badge
                  key={promo.id}
                  variant="outline"
                  className="w-full justify-start gap-2 bg-primary/5"
                >
                  <Tag className="h-3 w-3" />
                  <span className="truncate">{promo.name}</span>
                  <span className="ml-auto font-medium">
                    {promo.discountType === "percentage"
                      ? `${promo.discountValue}% off`
                      : `${formatPriceWithCurrency(
                          promo.discountValue,
                          currencyCode
                        )} off`}
                  </span>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Product Integration */}
        {plan.productId && (
          <div className="pt-3 border-t">
            <h4 className="text-sm font-medium mb-2">Product Integration</h4>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-500" />
              <span className="text-sm text-muted-foreground">
                Product ID: {plan.productId}
              </span>
            </div>
          </div>
        )}
      </CardContent>

      <CardFooter className="p-3 flex justify-end gap-2 bg-muted/10 border-t">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-3"
          onClick={() => onToggleActive(plan)}
        >
          {plan.isActive ? "Deactivate" : "Activate"}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onEdit(plan)}
        >
          <Edit2 className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={() => onDelete(plan)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
}
